import { move } from '@salescore/buff-common'
import type { ViewFieldsFragment } from '@salescore/client-api'
import {
  between,
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
} from '@salescore/core'
import { Array, Order, pipe } from 'effect'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import type { RSheetColumn, RSheetRecordNode } from '../domain/column'
import { type ColumnKey, usePivotColumns } from '../hooks/usePivotColumns'
import type { RSheetsNodeBlock } from '../rsheet/recoil/selectors/rSheetsNodeBlock'
import { kpiParameterAtom } from './navigation/atoms'
import { configAtom, type Side, useMoveFieldMutation } from './viewConfig'

export const defaultNodeBlock: RSheetsNodeBlock = {
  nodePath: [],
  labels: [],
  colors: [],
  helpTexts: [],
}

const calcNewColumnIndex = (oldColumnIndex: number, movingOntoIndex: number, side: Side): number => {
  if (movingOntoIndex >= oldColumnIndex) {
    const sideOffset = side === 'left' ? -1 : 0
    return movingOntoIndex + sideOffset
  } else {
    const sideOffset = side === 'left' ? 0 : 1
    return movingOntoIndex + sideOffset
  }
}

export const useMoveColumnMutation = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void,
) => {
  const config = useRecoilValue(configAtom)
  const setConfig = useSetRecoilState(configAtom)
  const { pivotColumns, columnKeys, rowColumns, systemColumns } = usePivotColumns(compiledConfig, view)
  const parameter = useRecoilValue(kpiParameterAtom)
  const setMoveField = useMoveFieldMutation()
  const setKpiParameter = useSetRecoilState(kpiParameterAtom)

  return ({
    oldColumnIndex,
    movingOntoIndex,
    side,
  }: {
    oldColumnIndex: number
    movingOntoIndex: number
    side: Side
    // eslint-disable-next-line complexity,sonarjs/cognitive-complexity
  }): void => {
    const newColumnIndex = calcNewColumnIndex(oldColumnIndex, movingOntoIndex, side)

    if (config.type === 'sheet') {
      const oldFieldIndex = columns[oldColumnIndex]?.configIndex
      const newFieldIndex = columns[newColumnIndex]?.configIndex
      if (oldFieldIndex === undefined || newFieldIndex === undefined) {
        return
      }

      setMoveField({ oldFieldIndex, newFieldIndex })
      setHasChangeToViewSchema(true)
      return
    }
    if (config.type === 'kpiPivot' || config.type === 'kpi') {
      const oldIndexType = oldColumnIndex < systemColumns.length + rowColumns.length ? 'row' : 'column'
      const newIndexType = newColumnIndex < systemColumns.length + rowColumns.length ? 'row' : 'column'

      // 行と行でドラッグしたケースでは、pivotのdimensionsを入れ替える
      if (oldIndexType === 'row' && newIndexType === 'row') {
        if (oldColumnIndex === newColumnIndex) {
          return
        }
        const oldRowIndex = rowColumns[oldColumnIndex - systemColumns.length]?.configIndex
        const newRowIndex = rowColumns[newColumnIndex - systemColumns.length]?.configIndex
        if (oldRowIndex === undefined || newRowIndex === undefined) {
          return
        }
        setKpiParameter({
          ...parameter,
          pivot: {
            ...parameter.pivot,
            rows: move(parameter.pivot.rows, oldRowIndex, newRowIndex),
          },
        })
        return
      }

      // これより先では、列同士の移動のみを許可
      if (oldIndexType !== 'column' || newIndexType !== 'column') {
        return
      }

      // 列と列でドラッグしたケースでは、列のソートを設定する
      // ソートロジックが難解で不具合になりやすいので、一旦KPIしか表示していないケースを専用で処理する
      const isKpiOnly =
        parameter.pivot.columns.at(-1)?.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key &&
        parameter.pivot.columns.length === 1
      const isKpiAndKpiGroupOnly =
        parameter.pivot.columns.at(-1)?.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key &&
        parameter.pivot.columns[0]?.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_DIMENSION().key &&
        parameter.pivot.columns.length === 2
      if (config.type === 'kpiPivot' && (isKpiOnly || isKpiAndKpiGroupOnly)) {
        const oldConfigIndex = pivotColumns[oldColumnIndex - systemColumns.length - rowColumns.length]?.configIndex
        const newConfigIndex = pivotColumns[newColumnIndex - systemColumns.length - rowColumns.length]?.configIndex
        const movingOntoConfigIndex =
          pivotColumns[between(movingOntoIndex - systemColumns.length - rowColumns.length, 0, pivotColumns.length - 1)]
            ?.configIndex

        if (oldConfigIndex === undefined || newConfigIndex === undefined) {
          return
        }
        // 同じ親を持つkeyの配列を取得、そのkeyの中でまず並び替えする
        const sortedKpis = move(config.kpis, oldConfigIndex, newConfigIndex)

        const afterSortNewIndexKpi = sortedKpis[newConfigIndex]
        if (afterSortNewIndexKpi === undefined) {
          // ありえないはず
          return
        }

        if (movingOntoConfigIndex !== undefined) {
          const movingOntoKpi = config.kpis[movingOntoConfigIndex]
          setConfig({
            ...config,
            kpis: [
              ...sortedKpis.slice(0, newConfigIndex),
              {
                viewId: afterSortNewIndexKpi.viewId,
                kpiGroupName: movingOntoKpi?.kpiGroupName ?? afterSortNewIndexKpi.kpiGroupName,
              },
              ...sortedKpis.slice(newConfigIndex + 1),
            ],
          })
          setHasChangeToViewSchema(true)
        }
        return
      }

      if (
        config.type === 'kpiPivot' &&
        parameter.pivot.columns.at(-1)?.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key
      ) {
        // KPI が分析軸の一番下にある場合はここに到達する
        // XXX: 例えば、ダッシュボードのうち、ある KPI が未設定で分析列を 部 > KPI にすると挙動がおかしくなる。
        // その原因は config.kpis で configIndex が 1 から始まってしまうからである。
        // このバグを直すためには config.kpis で configIndex が 0 から始まるように直すべきで、ここで対処すべきでない。

        // 分析列X > KPI グループ > KPI の順番の場合も、分析列X > KPI の順番の場合も、
        // 分析列X を基準に列の移動の可否を決定するため、 `parentOffset` を定義する
        const parentOffset =
          parameter.pivot.columns.at(-2)?.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME ? -1 : -2
        const oldIndex = oldColumnIndex - systemColumns.length - rowColumns.length
        const newIndex = newColumnIndex - systemColumns.length - rowColumns.length
        const movingOntoIndexRounded = between(
          movingOntoIndex - systemColumns.length - rowColumns.length,
          -1, // 分析軸やシステムカラム上にドロップした場合は-1にしておくことで、後に oldParentsとmovingOntoParentsが異なると判定される
          pivotColumns.length - 1,
        )
        const oldKey = columnKeys[oldIndex]
        const movingOntoKey = columnKeys[movingOntoIndexRounded]
        const oldParents = oldKey?.slice(0, parentOffset)
        const movingOntoParents = movingOntoKey?.slice(0, parentOffset)
        if (
          oldParents === undefined ||
          movingOntoParents === undefined ||
          !Array.makeEquivalence<ColumnKey>(
            (oldColumnKey, newColumnKey) =>
              oldColumnKey.label === newColumnKey.label && oldColumnKey.value === newColumnKey.value,
          )(oldParents, movingOntoParents)
        ) {
          // oldParentsとmovingOntoParentsが異なる場合は、何もしない
          return
        }
        const oldConfigIndex = pivotColumns[oldIndex]?.configIndex
        const newConfigIndex = pivotColumns[newIndex]?.configIndex
        const movingOntoConfigIndex = pivotColumns[movingOntoIndexRounded]?.configIndex
        if (oldConfigIndex === undefined || newConfigIndex === undefined) {
          return
        }
        // 同じ親を持つkeyの配列を取得、そのkeyの中でまず並び替えする
        const eq = Array.makeEquivalence<ColumnKey>((a, b) => a.value === b.value && a.label === b.label)
        const sameParentKeys = columnKeys.filter((x) => eq(x.slice(0, parentOffset), oldParents))
        const swapped = move(sameParentKeys, oldConfigIndex, newConfigIndex)
        const sortedKpis = pipe(
          config.kpis,
          Array.sortBy(Order.mapInput(Order.Number, (kpi) => swapped.findIndex((x) => x.at(-1)?.value === kpi.viewId))),
        )

        const afterSortNewIndexKpi = sortedKpis[newConfigIndex]
        if (afterSortNewIndexKpi === undefined || movingOntoConfigIndex === undefined) {
          // ありえないはず
          return
        }
        const movingOntoIndexKpi = config.kpis[movingOntoConfigIndex]
        if (movingOntoIndexKpi === undefined) {
          // ありえないはず
          return
        }

        setConfig({
          ...config,
          kpis: [
            ...sortedKpis.slice(0, newConfigIndex),
            {
              viewId: afterSortNewIndexKpi.viewId,
              kpiGroupName: movingOntoIndexKpi.kpiGroupName ?? afterSortNewIndexKpi.kpiGroupName,
            },
            ...sortedKpis.slice(newConfigIndex + 1),
          ],
        })
        setHasChangeToViewSchema(true)
      } else {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: sorterとしてセット
      }
    }
  }
}
